import type { CSSProperties, FC, ReactNode } from 'react';
import { observer } from 'mobx-react';

import { Picture, Tooltip, Popover, Modal } from '@ui';
import SubscribeProduct from '@components/SubscribeProduct';
import useWindowSize from '@customHooks/useWindowSize';
import { twcls } from '@utils/twcls';
import icons from '@icons';
import clsx from 'clsx';
import UIicons from '@ui-kit';
import { useStores } from '@root/src/stores';
import './index.scss';

const { minus, plus } = UIicons;

const {
  plus: { white: plusWhiteOutlined },
  minus: { udg: minusUDGOutlined }
} = icons.Outline;

interface BuyButtonProps {
  canBeBought: boolean;
  mini?: boolean;
  blockDeleteOnMinus?: boolean;
  style?: CSSProperties;
  isPreorder?: string;
  isWhite?: boolean;
  className?: string;
  btnTitle?: string;
  cartItemId?: number;
  productId: number;
  available: boolean;
  mainBtnClassName?: string;
  disabled?: boolean;
  onDisabledButtonClick?: () => void;
  productsAmountToShow?: number;
  isDigital?: boolean;
  isMiniCart?: boolean;
  canSubscribe?: boolean;
  onlyPlusButton?: boolean;
}
const BuyButton: FC<BuyButtonProps> = observer(
  ({
    canBeBought,
    canSubscribe = true,
    mini,
    blockDeleteOnMinus,
    style,
    isPreorder,
    onlyPlusButton,
    isWhite,
    className,
    btnTitle,
    cartItemId,
    productId,
    available = true,
    mainBtnClassName,
    disabled,
    onDisabledButtonClick,
    productsAmountToShow,
    isDigital,
    isMiniCart
  }) => {
    const { cartStore, modalsStore } = useStores();
    const { productsAmount, updateCart, changeProductsAmount } = cartStore;
    const { openDigitalModal } = modalsStore;

    const isMinusDisabled =
      blockDeleteOnMinus && productsAmount[productId] === 1;

    const updateCartHandler = (productId: number, number: number): void => {
      changeProductsAmount(number, productId);
      updateCart(productId, productsAmount[productId], cartItemId);
    };

    const buyBtnClickHandler = (): void => {
      if (isDigital) {
        openDigitalModal(productId);

        return;
      }
      if (!disabled) {
        updateCartHandler(productId, 1);
      } else {
        if (onDisabledButtonClick) {
          onDisabledButtonClick();
        }
      }
    };

    const productAmount = productsAmountToShow || productsAmount[productId];

    if (!canBeBought) {
      return null;
    }

    if (available) {
      return (
        <BuyBtn
          mini={mini}
          btnTitle={btnTitle}
          isMinusDisabled={isMinusDisabled}
          isPreorder={isPreorder}
          isWhite={isWhite}
          onlyPlusButton={onlyPlusButton}
          mainBtnClassName={mainBtnClassName}
          className={className}
          productId={productId}
          productAmount={productAmount}
          buyBtnClickHandler={buyBtnClickHandler}
          style={style}
          disabled={disabled}
          updateCartHandler={updateCartHandler}
          isDigital={isDigital}
          isMiniCart={isMiniCart}
        />
      );
    }
    if (canSubscribe) {
      return (
        <BuyBtnSubscribePopup className={className} productId={productId} />
      );
    }
    return null;
  }
);

interface BuyBtnProps {
  mini?: boolean;
  isMinusDisabled?: boolean;
  isPreorder?: string;
  style?: CSSProperties;
  isWhite?: boolean;
  className?: string;
  productId: number;
  btnTitle?: string;
  productAmount: number;
  mainBtnClassName?: string;
  buyBtnClickHandler: () => void;
  disabled?: boolean;
  updateCartHandler: (productId: number, count: number) => void;
  isDigital?: boolean;
  isMiniCart?: boolean;
  onlyPlusButton?: boolean;
}

const BuyBtn: FC<BuyBtnProps> = observer(
  ({
    mini,
    isMinusDisabled,
    isPreorder,
    style,
    isWhite,
    className,
    productId,
    productAmount,
    onlyPlusButton,
    mainBtnClassName,
    buyBtnClickHandler,
    disabled,
    btnTitle,
    updateCartHandler,
    isDigital,
    isMiniCart
  }) => (
    <div
      className={twcls(
        `BuyBtn${className ? ` BuyBtn--${className}` : ''}`,
        mini && ' BuyBtn--mini',
        onlyPlusButton && 'BuyBtn--only-plus'
      )}
      style={style}
    >
      {!productAmount ? (
        <Tooltip
          on={isPreorder ? ['hover', 'focus'] : []}
          offsetY={0}
          positionImp='top center'
          className='ProductBuyBtns__plus--tooltip'
          arrow
          tooltipText={isPreorder ? `Предзаказ на ${isPreorder}` : 'Предзаказ'}
        >
          {onlyPlusButton ? (
            <button
              className={clsx(
                'BuyBtn__control-btn BuyBtn__control-btn--plus',
                isMiniCart && 'BuyBtn__control-btn--mini-cart',
                onlyPlusButton && 'BuyBtn__control-btn--only-plus'
              )}
              onClick={() => {
                updateCartHandler(productId, 1);
              }}
            >
              <Picture
                icon={isMiniCart ? plus.x3 : plusWhiteOutlined.x3}
                height={onlyPlusButton ? '16' : mini ? '20' : '24'}
                width={onlyPlusButton ? '16' : mini ? '20' : '24'}
                className='BuyBtn__control-btn__increase__plus'
              />
            </button>
          ) : (
            <button
              onClick={buyBtnClickHandler}
              className={twcls(
                'BuyBtn__main-btn',
                disabled && 'BuyBtn__main-btn--disabled',
                isWhite && 'BuyBtn__main-btn--white',
                isPreorder && 'BuyBtn__main-btn--preOrder',
                mainBtnClassName
              )}
            >
              {isDigital ? 'Оплатить' : btnTitle || 'Купить'}
            </button>
          )}
        </Tooltip>
      ) : (
        <div
          className={`BuyBtn__control-btns${
            disabled ? ' BuyBtn__control-btns--disabled' : ''
          }`}
        >
          <button
            disabled={isMinusDisabled}
            className={clsx(
              'BuyBtn__control-btn',
              isMiniCart && 'BuyBtn__control-btn--mini-cart',
              onlyPlusButton && 'BuyBtn__control-btn--only-plus'
            )}
            onClick={() => {
              updateCartHandler(productId, -1);
            }}
          >
            <Picture
              icon={isMiniCart ? minus.x3 : minusUDGOutlined.x3}
              iconHover={minusUDGOutlined.x3}
              height={onlyPlusButton ? '16' : mini ? '20' : '24'}
              width={onlyPlusButton ? '16' : mini ? '20' : '24'}
              className='BuyBtn__control-btn__reduce__minus'
            />
          </button>
          <div className='BuyBtn__product-amount'>{productAmount}</div>
          <button
            className={clsx(
              'BuyBtn__control-btn BuyBtn__control-btn--plus',
              isMiniCart && 'BuyBtn__control-btn--mini-cart',
              onlyPlusButton && 'BuyBtn__control-btn--only-plus'
            )}
            onClick={() => {
              updateCartHandler(productId, 1);
            }}
          >
            <Picture
              icon={isMiniCart ? plus.x3 : plusWhiteOutlined.x3}
              height={onlyPlusButton ? '16' : mini ? '20' : '24'}
              width={onlyPlusButton ? '16' : mini ? '20' : '24'}
              className='BuyBtn__control-btn__increase__plus'
            />
          </button>
        </div>
      )}
    </div>
  )
);

interface BuyBtnSubscribePopupProps {
  productId: number;
  className?: string;
}

const BuyBtnSubscribePopup: FC<BuyBtnSubscribePopupProps> = observer(
  ({ productId, className }) => {
    const { isMobile } = useWindowSize();
    const styleObj = isMobile
      ? {
          zIndex: 'var(--z-modal-overlay)',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(4px)'
        }
      : {
          zIndex: 'var(--z-modal-overlay)'
        };
    const trigger = (
      <div className={`BuyBtn ${className ? ` BuyBtn--${className}` : ''}`}>
        <div className='BuyBtn__subscribe-btn'>Подписка</div>
      </div>
    );

    const renderContent = (close: () => void): ReactNode => (
      <SubscribeProduct
        productId={productId}
        withPaddingAndBorderRadius
        closeModal={close}
        isModal
        subscriptionType='in_stock'
      />
    );

    // Mobile: centered modal. Desktop: anchored popover next to the subscribe button.
    if (isMobile) {
      return (
        <Modal withoutCross className='pop-subscribe' popupProps={{ trigger }}>
          {renderContent}
        </Modal>
      );
    }

    return (
      <Popover
        on='click'
        position={className === 'full-width' ? 'top center' : 'left top'}
        offsetY={className === 'full-width' ? 25 : 0}
        repositionOnResize
        className='pop-subscribe'
        overlayStyle={styleObj}
        contentStyle={{
          zIndex: 'var(--z-modal-content)'
        }}
        trigger={trigger}
      >
        {renderContent}
      </Popover>
    );
  }
);

export default BuyButton;
